import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { PRODUCT_LIST_PAGE_TYPE } from '../../constants';
import RosettaFormattedMessage from '../common/rosetta_formatted_message';
import Link from 'react-router/lib/Link';
import Lazy from '../common/lazy';
import ResponsiveImg from '../common/responsive_img';
import ProductLink from '../common/product_link';
import { sendAnalyticsEvent } from '../../utils/analytics';
import padPrice from '../../utils/padPrice';
import getCapitalizedName from '../../utils/getCapitalizedName';
import classNames from 'classnames';
import {get, isFinite} from 'lodash';
import truncateHtml from '../../utils/truncateHtml';

const SPONSORED_PRODUCT_LABEL = 'promoted';
const LAZY_LOAD_CUSHION_PX = 0;

export default class ProductCard extends Component {
  state = { isLoaded: false };

  handleImgLoad = () =>
    this.setState({ isLoaded: true });

  handlePurchaseClick = () => {
    sendAnalyticsEvent(
      'Shopping',
      'Related Products',
      `${this.props.id} - Outbound`,
      { nonInteraction: false }
    );
  };

  handleMoreInfoClick = () => sendAnalyticsEvent(
    'Shopping',
    'Related Products',
    `${this.props.id} - PDP`,
    { nonInteraction: false }
  );

  handleImpressionTrackerLoad = () => sendAnalyticsEvent(
    'Shopping',
    'Impression',
    this.props.id,
    { nonInteraction: true }
  );

  padPrice = (price) => padPrice(
    this.props.offer.currency,
    price
  );

  impressionTracker = () =>
    <Lazy>
      <img className="impression-tracker"
        src={this.props.impression_tracker}
        onLoad={this.handleImpressionTrackerLoad}
      />
    </Lazy>

  sponsoredLabel = () =>
    <div className="sponsored-label"
      dangerouslySetInnerHTML={{ __html: SPONSORED_PRODUCT_LABEL }}
    />

  render() {
    const { name, page_type, is_sponsored, product_url, path, short_name,
      thumbnail_url, click_tracker, impression_tracker, brand,
      offer: { price, sale_price, currency, retailer }, is_sem3_available, images } = this.props;

    const infoClassName = classNames('product card', {
      'sale': isFinite(sale_price),
      'sponsored': is_sponsored,
      'product-list': page_type === PRODUCT_LIST_PAGE_TYPE
    });

    const imageContainerClass = classNames('cropping-container', { 'loading': !this.state.isLoaded });

    const productLinkProps = {
      click_tracker,
      product_url,
      delegatedOnClick: this.handlePurchaseClick
    };

    const productName = short_name || name;
    const truncatedProductName = truncateHtml(
      getCapitalizedName(productName), { length: 80, omission: '...' }
    );

    const firstProductImage = images[0] || {};
    const { url, width, height } = firstProductImage;

    const productImageProps = {
      src: (height && width) ? url : thumbnail_url,
      onLoad: this.handleImgLoad,
      height: height,
      width: width,
      optimizeOrientation: (height && width) ? true : false
    };

    const retailerCredit = (
      <span className="retailer-credit">
        <span className="retailer"
          dangerouslySetInnerHTML={{ __html: get(retailer, 'name') }}
        />
      </span>
    );

    const isAvailable = is_sem3_available;

    // This is a temporary fix for the Wordpress launch.
    const shouldShowProductInfoButton = false;

    return (
      <div className={infoClassName}>
        {
          shouldShowProductInfoButton &&
          path &&
          <Link to={path}
            className={classNames('more-info', { 'full-width': !isAvailable })}
            onClick={this.handleMoreInfoClick}
          >
            <div className="more-info-text">
              <RosettaFormattedMessage parentFilename={__filename}
                id="details"
                description="Message that appears on a product card details link"
                defaultMessage="DETAILS"
              />
            </div>
          </Link>
        }
        <ProductLink {...productLinkProps}>
          {!isAvailable &&
            <div className="sold-out-overlay">
              <RosettaFormattedMessage parentFilename={__filename}
                id="unavailable"
                description="Message that appears on a product card when unavailable"
                defaultMessage="SOLD OUT"
              />
            </div>
          }
          <Lazy cushion={LAZY_LOAD_CUSHION_PX} className={imageContainerClass}>
            <ResponsiveImg {...productImageProps} />
          </Lazy>
          <div className="content">
            {is_sponsored && this.sponsoredLabel()}
            <div className="brand-name"
              dangerouslySetInnerHTML={{ __html: get(brand, 'name') }}
            />
            <div className="name"
              dangerouslySetInnerHTML={{ __html: truncatedProductName }}
            />
            <div className="buy-details">
              {isAvailable &&
                <button className="buy-button">
                  <RosettaFormattedMessage parentFilename={__filename}
                    id="buy_button"
                    description="Message that appears on a product card buy button"
                    defaultMessage="BUY"
                  />
                </button>
              }
              <div className="price-details">
                <div className="price">
                  <span className="sale-price">
                    {`${padPrice(currency, sale_price)}`}
                  </span>
                  <span className="retail-price">
                    {`${padPrice(currency, price)}`}
                  </span>
                </div>
                {retailerCredit}
              </div>
            </div>
          </div>
        </ProductLink>
        {impression_tracker && this.impressionTracker()}
      </div>
    );
  }
}

ProductCard.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string.isRequired,
  is_sponsored: PropTypes.bool.isRequired,
  product_url: PropTypes.string.isRequired,
  path: PropTypes.string,
  short_name: PropTypes.string,
  thumbnail_url: PropTypes.string.isRequired,
  offer: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    sale_price: PropTypes.number
  }),
  brand: PropTypes.shape({
    name: PropTypes.string.isRequired
  }),
  click_tracker: PropTypes.string,
  impression_tracker: PropTypes.string,
  page_type: PropTypes.string,
  is_sem3_available: PropTypes.bool,
  images: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number
  })),
  category: PropTypes.object
};

